import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BaseWorkTaskTypeEnum, TechnicianScheduleDTO, UserDTO, WorkTaskStatus } from '../../api/api';
import DatePicker from '../../components/date-picker/DatePicker';
import { useNavigateToTask } from '../../hooks/useNavigateToTask';
import TaskService from '../../services/TaskService';
import { formatDateString } from '../../utils/dateHandling';
import UserService from '../../services/UserService';
import { log } from '../../utils/logging/log';
import NotificationService from '../../services/NotificationService';
import Select, { MenuItem } from '../../components/select/Select';
import Typography from '../../components/typography/Typography';
import { StyledHeader } from '../time-reg-view/TimeRegViewDefault';
import SelectedUsersService from '../../services/SelectedUsersService';
import LoadingOverlay from '../../components/loading-overlay/LoadingOverlay';

const ScheduleView = () => {
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(100);
  const [data, setData] = useState<TechnicianScheduleDTO[]>();
  const [filteredData, setFilteredData] = useState<TechnicianScheduleDTO[]>();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const stageRef = useRef<HTMLInputElement>(null);
  const navigateToTask = useNavigateToTask();
  const [users, setUsers] = useState<UserDTO[]>([]);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // start day at 06:00, thus time part is thrown away and 6 hours added.
  const startOfDayMs = new Date(selectedDate.toDateString()).getTime() + 21600000;
  // sets the "wideness" of the day and blocks
  const dayInMs = 86400000 / 2.0;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      UserService.getAllKnownUsers(),
      SelectedUsersService.getAllSelectedUsers(),
      TaskService.getSchedule(new Date().toISOString())
    ])
      .then((res) => {
        setUsers(res[0]);
        setSelectedUsers(res[1]);
        setData(res[2]);
      })
      .catch((err) => {
        log(err);
        NotificationService.error('Kunne ikke hente medarbejdere');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (stageRef.current) {
      setWidth(stageRef.current.offsetWidth);
    }
  }, [selectedDate, stageRef]);

  useEffect(() => {
    if (selectedUsers.length === 0) {
      setFilteredData(data);
    } else {
      setFilteredData(data?.filter((obj) => selectedUsers.some((user) => user === obj.name)));
    }
  }, [data, selectedUsers]);

  const handleDateChange = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      TaskService.getSchedule(date.toISOString()).then((evens: TechnicianScheduleDTO[]) => {
        setData(evens);
      });
    },
    [setSelectedDate]
  );

  const handleOnClickTask = useCallback(
    (workTaskId: number | undefined, baseWorkTaskType: BaseWorkTaskTypeEnum | undefined) => {
      if (workTaskId === undefined || baseWorkTaskType === undefined) return;
      navigateToTask(workTaskId, baseWorkTaskType);
    },
    [navigateToTask]
  );

  const getColorFromStats = (status: WorkTaskStatus | undefined) => {
    let color = '#a3a3a3';
    switch (status) {
      case WorkTaskStatus.Created:
        color = '#8266E6';
        break;
      case WorkTaskStatus.ReadyForPlanning:
        color = '#557FFE';
        break;
      case WorkTaskStatus.Planned:
        color = '#FFBB89';
        break;
      case WorkTaskStatus.PlannedAppointment:
        color = '#FF7A19';
        break;
      case WorkTaskStatus.OnRoute:
        color = '#FFEE52';
        break;
      case WorkTaskStatus.Ongoing:
        color = '#79C240';
        break;
      case WorkTaskStatus.Pause:
        color = '#DC4040';
        break;
      case WorkTaskStatus.Completed:
        color = '#60935D';
        break;
      case WorkTaskStatus.Processed:
        color = '#C7C7C7';
        break;
    }
    return color;
  };

  const setSelectedUsersHandler = (users: string[]) => {
    SelectedUsersService.updateSelectedUsers(users)
      .then((response) => {
        setSelectedUsers(response);
      })
      .catch((error) => {
        NotificationService.error('Kunne ikke vælge mine medarbejdere');
        log(error);
      });
  };

  return (
    <>
      {loading ? (
        <LoadingOverlay />
      ) : (
        <Container>
          <StyledHeader>
            <Typography textAlign="left" variant="h2">
              Oversigt
            </Typography>
            <Row>
              <DatePicker
                value={formatDateString(selectedDate.toISOString())}
                label="Dato"
                onDateChanged={(date) => handleDateChange(date)}
                disableWeekends
                sx={{ width: '300px', marginRight: '20px' }}
              />
              <Select
                fullWidth
                value={selectedUsers}
                label="Medarbejdere"
                onChange={(e: any) => setSelectedUsersHandler(e.target.value)}
                multiple
                sx={{ width: '300px' }}
              >
                {users.map((u) => (
                  <MenuItem value={u.email} key={u.email}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </Row>
          </StyledHeader>
          <InnerContainer>
            <div id="div1" ref={stageRef}>
              {filteredData &&
                filteredData.map((t) => {
                  return (
                    <StyledInnerContainer>
                      <NameText>{users.find((user) => user.email === t.name)?.name ?? t.name}</NameText>
                      <Timeline>
                        {t.events &&
                          t.events.map((tt) => {
                            var start = tt.taskStart ? new Date(tt.taskStart) : new Date();
                            var end = tt.taskEnd ? new Date(tt.taskEnd) : new Date();
                            var plannedArrival = tt.plannedArrival ? new Date(tt.plannedArrival) : new Date();
                            var workTaskId = tt.workTaskId ?? '-';

                            // Warning: Spicy math below!
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  position: 'relative'
                                }}
                              >
                                <TimeSlot
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    background: '#aaaaaa',
                                    left: ((width - 400) / dayInMs) * (start.getTime() - startOfDayMs),
                                    width: ((width - 400) / dayInMs) * (plannedArrival.getTime() - start.getTime())
                                  }}
                                ></TimeSlot>
                                <TimeSlot
                                  onClick={() => handleOnClickTask(tt.workTaskId, tt.baseWorkTaskType)}
                                  style={{
                                    background: getColorFromStats(tt.status),
                                    left: ((width - 400) / dayInMs) * (plannedArrival.getTime() - startOfDayMs),
                                    width: ((width - 400) / dayInMs) * (end.getTime() - plannedArrival.getTime())
                                  }}
                                >
                                  {workTaskId}
                                </TimeSlot>
                              </div>
                            );
                          })}
                      </Timeline>
                    </StyledInnerContainer>
                  );
                })}
            </div>
          </InnerContainer>
        </Container>
      )}
    </>
  );
};

const Container = styled.div`
  background-color: white;
  border-left: 1px solid #aaa;
  border-top: 1px solid #aaa;
`;

const InnerContainer = styled.div`
  padding: 0px 20px;
`;

const StyledInnerContainer = styled.div`
  display: flex;
  text-align: left;
  height: 40px;
  border-bottom: 1px solid grey;
`;

const NameText = styled.div`
  width: 200px;
  flex-direction: column;
  display: flex;
  justify-content: center;
  font-size: 16px;
`;

const Timeline = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const TimeSlot = styled.div`
  position: absolute;
  background-color: #398587;
  display: flex;
  align-items: center;
  padding-left: 10px;
  border-radius: 5px;
  height: 30px;

  &:hover {
    cursor: pointer;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

export default ScheduleView;
